import { LinkExternalIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import { cloneElement, MouseEvent } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReportHistoryTimeline from 'dashboard/components/ReportHistoryTimeline';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import {
  FormReportFlagIssues,
  FormReportFlagIssuesData,
} from 'dashboard/forms/FormReportFlagIssues';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  DISMISSIBLE_REPORT_TYPES,
  EXPORTABLE_REPORT_TYPES,
  RECURRING_CA_REPORT_TYPES,
  RECURRING_REPORT_TYPES,
  Report,
  ReportStatus,
} from 'dashboard/models/report';
import { ReportAdverseMedia } from 'dashboard/models/report/adverse-media';
import { ReportPoliticallyExposedPerson } from 'dashboard/models/report/politically-exposed-person';
import { ReportWatchlist } from 'dashboard/models/report/watchlist';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  pauseReport,
  printReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
} from 'dashboard/redux/thunk-actions/report';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const StyledMenu = styled(DropdownMenu)`
  min-width: 200px;
`;

interface ReportMenuProps {
  report: Report;
}

const ConnectedReportMenu = (props: ReportMenuProps & PropsFromRedux) => {
  const { report } = props;

  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);

  const [openRunHistoryModal, { enable: showRunHistoryModal, disable: hideRunHistoryModal }] =
    useToggle(false);

  const [flagReportModal, { enable: showFlagReportModal, disable: hideFlagReportModal }] =
    useToggle(false);

  const [redactReportModal, { enable: showRedactReportModal, disable: hideRedactReportModal }] =
    useToggle(false);

  const isRecurringReport = RECURRING_REPORT_TYPES.includes(report.type);
  const isRecurringCAReport = RECURRING_CA_REPORT_TYPES.includes(report.type);
  const isRedacted = report?.attributes?.['redacted-at'] != null;
  const isReportWithIntervalSeconds =
    report?.attributes?.['report-template-version-interval-seconds'] != null;

  const isAdHocRunnableReport =
    // basic conditions for adhoc run
    isRecurringReport &&
    !isRedacted &&
    // special condition for CA reports because if there are not recurring, rerunning adhoc may not do anything because
    // we may have unmonitored the CA search to save money.
    (!isRecurringCAReport || isReportWithIntervalSeconds);
  const isDismissibleReport = DISMISSIBLE_REPORT_TYPES.includes(report.type) && !isRedacted;
  const isExportableReport = EXPORTABLE_REPORT_TYPES.includes(report.type);
  const isPauseable =
    report?.attributes?.['report-template-version-interval-seconds'] != null && !isRedacted;
  const isRedactable = !isRedacted && report.type !== ApiModel.ReportCustomList; // Disallow custom list reports to be redacted for now

  const isAdditionalInfoWatchlist =
    report.type === ApiModel.ReportWatchlist &&
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (report as ReportWatchlist)?.attributes['entity-lists']?.length > 0;

  // Run Report Adhoc
  const { error: runReportAdhocError, callback: handleRunReportAdhoc } =
    useApiCallback(async () => {
      props.runReportAdhoc(props.report.id);
      props.showMessage('Re-running this report.', MessageType.Success);
    }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: runReportAdhocError,
    onError: () =>
      props.showMessage('Failed to re-run report, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to re-run report, please try again.', MessageType.Failure),
  });

  // Pause Report
  const { error: pauseReportError, callback: handlePauseReport } = useApiCallback(async () => {
    await props.pauseReport(
      props.report.id,
      props.accountReports,
      props.inquiryReports,
      props.caseObjects,
    );
    props.showMessage('Successfully paused this report.', MessageType.Success);
  }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: pauseReportError,
    onError: () =>
      props.showMessage('Failed to pause report, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to pause report, please try again.', MessageType.Failure),
  });

  // Resume Report
  const { error: resumeReportError, callback: handleResumeReport } = useApiCallback(async () => {
    props.resumeReport(
      props.report.id,
      props.accountReports,
      props.inquiryReports,
      props.caseObjects,
    );
    props.showMessage('Successfully resumed this report.', MessageType.Success);
  }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: resumeReportError,
    onError: () =>
      props.showMessage('Failed to resume report, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to resume report, please try again.', MessageType.Failure),
  });

  // Print Report
  const { error: printReportError, callback: handlePrintReport } = useApiCallback(async () => {
    props.showMessage('Exporting Report as PDF, please wait...', MessageType.Notice),
      await props.printReport(props.report.id);
  }, [props.report, props.printReport]);

  useApiErrorHandler({
    error: printReportError,
    onError: () =>
      props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure),
  });

  // Flag Issue
  const { error: flagIssuesError, callback: handleFlagIssues } = useApiCallback(
    async ({ description }: FormReportFlagIssuesData) => {
      await SessionClientDashboard.postResourceAction<Report>(
        {
          id: props.report.id,
          type: ApiModel.Report,
        },
        'flag-issues',
        {
          meta: {
            description,
          },
        },
      );
      props.showMessage(
        'Thank you for reporting this issue! We review and track these issues to inform product improvements.',
        MessageType.Success,
      );
    },
    [props.report, props.showMessage],
  );

  useApiErrorHandler({
    error: flagIssuesError,
    onError: () =>
      props.showMessage('Failed to flag issue to report, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to flag issue to report, please try again.', MessageType.Failure),
  });

  // Redact Report
  const { error: redactReportError, callback: handleRedactReport } = useApiCallback(async () => {
    props.redactReport(
      props.report.id,
      props.accountReports,
      props.inquiryReports,
      props.caseObjects,
    );
    props.showMessage('Successfully redacted report.', MessageType.Success);
  }, [props.report, props.redactReport]);

  useApiErrorHandler({
    error: redactReportError,
    onError: () =>
      props.showMessage('Failed to redact report, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to redact report, please try again.', MessageType.Failure),
  });

  const reportHasIgnorableMatches = (() => {
    if (
      report.attributes.status === ReportStatus.Pending ||
      report.attributes.status === ReportStatus.Errored
    ) {
      return false;
    }

    switch (report.type) {
      case ApiModel.ReportAdverseMedia:
        // only allowing one related source for now, as multiple kicks you back to the reports details page
        return (report as ReportAdverseMedia).attributes['related-sources']?.length === 1;
      case ApiModel.ReportWatchlist:
        if (isAdditionalInfoWatchlist) {
          return (report as ReportWatchlist).attributes['entity-lists']?.length === 1;
        }
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return (report as ReportWatchlist).attributes['matched-lists']?.length > 0;
      case ApiModel.ReportPoliticallyExposedPerson:
        return (report as ReportPoliticallyExposedPerson).attributes['result']?.length > 0;
      default:
        return false;
    }
  })();

  const dismissMenuItems: JSX.Element[] = [];
  const recurMenuItems: JSX.Element[] = [];
  const historyMenuItems: JSX.Element[] = [];
  const exportMenuItems: JSX.Element[] = [];

  if (
    usePermission(Permission.ReportIgnoreHitsWrite) &&
    isDismissibleReport &&
    reportHasIgnorableMatches
  ) {
    dismissMenuItems.push(
      <DropdownMenu.Item
        id='report-menu__dismiss-matches-button'
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          showIgnoreHitsModal();
        }}
      >
        {isAdditionalInfoWatchlist ? 'Dismiss identity' : 'Dismiss matches'}
      </DropdownMenu.Item>,
    );
  }

  if (usePermission(Permission.ReportWrite) && isAdHocRunnableReport) {
    recurMenuItems.push(
      <DropdownMenu.Item
        id='report-menu__run-report-button'
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          handleRunReportAdhoc();
        }}
      >
        Re-run report
      </DropdownMenu.Item>,
    );
  }

  if (usePermission(Permission.ReportPauseWrite) && isPauseable) {
    recurMenuItems.push(
      <DropdownMenu.Item
        id='report-menu__pause-report-button'
        onClick={(e: MouseEvent) => {
          e.stopPropagation();

          if (report.attributes['recur-status'] === 'active') {
            handlePauseReport();
          } else {
            handleResumeReport();
          }
        }}
      >
        {`${
          report.attributes['recur-status'] === 'active' ? 'Pause' : 'Resume'
        } continuous monitoring`}
      </DropdownMenu.Item>,
    );
  }

  if (isRecurringReport) {
    historyMenuItems.push(
      <DropdownMenu.Item
        id='report-menu__run-history-button'
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          showRunHistoryModal();
        }}
      >
        Report History
      </DropdownMenu.Item>,
    );
  }

  if (isExportableReport) {
    exportMenuItems.push(
      <DropdownMenu.Item
        id='report-menu__export-pdf-button'
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          handlePrintReport();
        }}
      >
        Export PDF
      </DropdownMenu.Item>,
    );
  }

  [dismissMenuItems, recurMenuItems, historyMenuItems, exportMenuItems].forEach((menuGroup) => {
    if (menuGroup.length > 0) {
      menuGroup.push(<DropdownMenu.Divider />);
    }
  });

  const menuItems = [
    ...dismissMenuItems,
    ...recurMenuItems,
    ...historyMenuItems,
    ...exportMenuItems,
  ];

  menuItems.push(
    <DropdownMenu.Item id='report-menu__flag-issue-button' onClick={showFlagReportModal}>
      Flag issue
    </DropdownMenu.Item>,
  );

  if (usePermission(Permission.ReportRedact) && isRedactable) {
    menuItems.push(
      <DropdownMenu.Item
        id='report-menu__redact-button'
        variant='danger'
        onClick={showRedactReportModal}
      >
        Redact
      </DropdownMenu.Item>,
    );
  }

  menuItems.push(
    <DropdownMenu.Divider />,
    <DropdownMenu.Item
      id='report-menu__report-details-button'
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        showFlagReportModal();
      }}
      suffixIcon={<LinkExternalIcon />}
      as={Link}
      to={`/dashboard/reports/${report.id}`}
    >
      Report details
    </DropdownMenu.Item>,
  );

  const ignoreEntityId = (() => {
    switch (report.type) {
      case ApiModel.ReportAdverseMedia:
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return report.attributes['related-sources'][0]?.id;
      case ApiModel.ReportWatchlist:
        return report.attributes['entity-lists']?.[0]?.id;
      default:
        return null;
    }
  })();

  return (
    <>
      <StyledMenu
        trigger={
          <Button
            id='report-menu__open-menu-button'
            width='icon'
            variant='inverse'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MoreIcon />
          </Button>
        }
      >
        {menuItems.map((item, key) => cloneElement(item, { key }))}
      </StyledMenu>
      {isAdditionalInfoWatchlist ? (
        <IgnoreHitsModal
          report={report}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          entityId={(report as ReportWatchlist).attributes['entity-lists'][0].id}
          ignoreType='entity'
          modalHeaderText='Dismiss Identity Match?'
          ignoreHitsModalOpen={ignoreHitsModal}
          hideIgnoreHitsModal={hideIgnoreHitsModal}
        />
      ) : (
        <IgnoreHitsModal
          report={report}
          ignoreHitsModalOpen={ignoreHitsModal}
          hideIgnoreHitsModal={hideIgnoreHitsModal}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          entityId={ignoreEntityId}
          ignoreType='media'
        />
      )}
      <ModalWrapper isOpen={openRunHistoryModal} handleCloseModal={hideRunHistoryModal}>
        <ModalContainer size='lg'>
          <ModalHeader onClose={hideRunHistoryModal}>Report History</ModalHeader>
          <ModalBody>
            <ReportHistoryTimeline report={report} />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={flagReportModal} handleCloseModal={hideFlagReportModal}>
        <ModalContainer size='lg'>
          <ModalHeader onClose={hideFlagReportModal}>Flag an issue with this report</ModalHeader>
          <ModalBody>
            <p>
              If you see an issue with the content in this report, you can flag it to our QA team
              for review.
            </p>
            <FormReportFlagIssues
              onSubmit={(data: FormReportFlagIssuesData) => {
                handleFlagIssues(data);
                hideFlagReportModal();
              }}
              handleCancel={hideFlagReportModal}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={redactReportModal} handleCloseModal={hideRedactReportModal}>
        <ConfirmationModal
          cancelAction={hideRedactReportModal}
          confirmAction={() => {
            handleRedactReport();
            hideRedactReportModal();
          }}
          header='Redact this report’s PII?'
          confirmActionText='Redact'
        >
          Personally identifiable information will be redacted. This action is permanent and can’t
          be undone.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  inquiryReports: state.inquiry.reports,
  accountReports: state.account.reports,
  caseObjects: state.case.caseToken
    ? state.case.case[state.case.caseToken].caseDetails?.objects
    : [],
});

const mapDispatchToProps = {
  showMessage,
  pauseReport,
  printReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedReportMenu);
